import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, TablePagination, TableRow } from '@mui/material';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import TablePaginationCustom from 'src/components/Table/TablePaginationCustom';
import Table from 'src/components/Table';
import NotFoundRecords from 'src/components/NotFoundRecords';
import GlobalPaymentRow from './GlobalPaymentRow';

if (!process.env.REACT_APP_TABLE_SIZE) {
  console.error(
    'REACT_APP_TABLE_SIZE variable not loaded properly. Check your .env file please!'
  );
}

const Tbody = ({ transactions, onView }) =>
  transactions.map((transaction) => (
    <GlobalPaymentRow key={transaction.id} {...transaction} onView={onView} />
  ));

Tbody.propTypes = {
  transactions: PropTypes.array.isRequired,
  onView: PropTypes.func.isRequired
};

const GlobalPaymentTable = ({
  className,
  transactions,
  count,
  page,
  handleChangePage,
  loading,
  onView,
  ...rest
}) => {
  const { t } = useTranslation();
  const headColumnWith = '9%';

  const theads = useMemo(() => () => (
    <TableRow component="tr">
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.PAYMENT_DATE_COLUMN')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.COMPLEMENT_COLUMN')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.DETAIL.CLIENT')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.CONTRACT')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.PAYMENT_ORIGIN_TITLE')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.RELATED_INVOICES_COLUMN')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.PENDING_AFTER_COLUMN')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.RECEIVED_AMOUNT_COLUMN')}
      </TableCell>
      <TableCell style={{ width: '2%' }} align="center">
        <span
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0
          }}
        >
          {t('COMMON.ACTION')}
        </span>
      </TableCell>
    </TableRow>
  ));

  const tbody = useMemo(
    () => () => <Tbody transactions={transactions} onView={onView} />,
    [transactions, onView]
  );

  const pagination = useMemo(
    () => () => (
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count}
        rowsPerPage={+process.env.REACT_APP_TABLE_SIZE}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationCustom}
        labelDisplayedRows={({ from, to, count: c }) => {
          return `${from}-${to} ${t('COMMON.OF')} ${c}`;
        }}
      />
    ),
    [page, transactions]
  );

  if (!loading && count === 0) {
    return (
      <Card>
        <NotFoundRecords
          title="NOT_FOUND_RECORDS_MESSAGE.RECEIVED_PAYMENTS_TITLE"
          description="NOT_FOUND_RECORDS_MESSAGE.RECEIVED_PAYMENTS_DESCRIPTION"
        />
      </Card>
    );
  }

  return (
    <Table
      {...rest}
      tbody={tbody}
      theads={theads}
      pagination={pagination}
      loading={loading}
      skeletonLoading
      skeletonColumns={9}
    />
  );
};

GlobalPaymentTable.propTypes = {
  className: PropTypes.string,
  transactions: PropTypes.array.isRequired,
  count: PropTypes.number,
  page: PropTypes.number,
  handleChangePage: PropTypes.func,
  loading: PropTypes.bool,
  onView: PropTypes.func.isRequired
};

GlobalPaymentTable.defaultProps = {
  count: 0
};

export default GlobalPaymentTable;
